import React, { useState, useMemo } from 'react';
import { useSettings } from '../hooks/useSettings';
import { Spinner } from './Spinner';
import { EditIcon } from './icons/EditIcon';
import { DeleteIcon } from './icons/DeleteIcon';
import { ConfirmModal } from './ConfirmModal';
import { SafeUser } from '../types';
import { useAuth } from '../hooks/useAuth';

interface UserManagerProps {
    title: string;
}

export const UserManager: React.FC<UserManagerProps> = ({ title }) => {
    const { users, isLoading, addUser, updateUser, deleteUser } = useSettings();
    const { user: currentUser } = useAuth();
    
    // State for adding a new user
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [addError, setAddError] = useState('');
    
    const [filter, setFilter] = useState('');

    // State for deleting a user
    const [userToDelete, setUserToDelete] = useState<SafeUser | null>(null);

    // State for editing a user
    const [userToEdit, setUserToEdit] = useState<SafeUser | null>(null);
    const [editUsername, setEditUsername] = useState('');
    const [editPassword, setEditPassword] = useState('');
    const [editPasswordConfirm, setEditPasswordConfirm] = useState('');
    const [editError, setEditError] = useState('');
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

    const filteredUsers = useMemo(() => 
        users.filter(user => user.username.toLowerCase().includes(filter.toLowerCase()))
    , [users, filter]);

    const handleAddSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setAddError('');
        if (password !== confirmPassword) {
            setAddError('Hesla se neshodují.');
            return;
        }
        if (password.length < 6) {
            setAddError('Heslo musí mít alespoň 6 znaků.');
            return;
        }

        console.log(`[FE LOG] UserManager: Step 1 - Calling addUser for user "${username}".`);
        const newUser = await addUser(username, password);
        if (newUser) {
            console.log('[FE LOG] UserManager: Step 4 - addUser call succeeded. Response:', newUser);
            setUsername('');
            setPassword('');
            setConfirmPassword('');
        } else {
            console.log('[FE LOG] UserManager: Step 4 - addUser call failed (returned false).');
            setAddError('Uživatel s tímto jménem již existuje.');
        }
    };
    
    const handleEditClick = (user: SafeUser) => {
        setUserToEdit(user);
        setEditUsername(user.username);
        setEditPassword('');
        setEditPasswordConfirm('');
        setEditError('');
    };

    const handleConfirmDelete = () => {
        if (userToDelete && userToDelete.username !== 'admin' && userToDelete.id !== currentUser?.id) {
            deleteUser(userToDelete.id);
        }
        setUserToDelete(null);
    };

    const handleConfirmEdit = async () => {
        if (!userToEdit) return;
        setEditError('');
        
        if (editPassword && editPassword.length < 6) {
            setEditError('Nové heslo musí mít alespoň 6 znaků.');
            return;
        }
        if (editPassword !== editPasswordConfirm) {
            setEditError('Nová hesla se neshodují.');
            return;
        }
        if (!editUsername.trim()) {
            setEditError('Uživatelské jméno nesmí být prázdné.');
            return;
        }

        const updates: { username?: string; newPassword?: string } = {};
        if (editUsername.trim() !== userToEdit.username) {
            updates.username = editUsername.trim();
        }
        if (editPassword) {
            updates.newPassword = editPassword;
        }
        
        if (Object.keys(updates).length > 0) {
            setIsSubmittingEdit(true);
            const result = await updateUser(userToEdit.id, updates);
            setIsSubmittingEdit(false);

            if (!result.success) {
                setEditError(result.error || 'Neznámá chyba při aktualizaci.');
            } else {
                setUserToEdit(null);
            }
        } else {
            setUserToEdit(null);
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-1 bg-gray-50 p-6 rounded-lg self-start">
                <h3 className="text-xl font-semibold text-gray-700 mb-4">Přidat nového uživatele</h3>
                <form onSubmit={handleAddSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Uživatelské jméno</label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Heslo</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Potvrdit heslo</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            required
                        />
                    </div>
                    {addError && <p className="text-sm text-red-600">{addError}</p>}
                    <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Přidat uživatele
                    </button>
                </form>
            </div>
            <div className="md:col-span-2 relative">
                {isLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 z-10 flex items-center justify-center rounded-lg">
                        <Spinner text="Aktualizuji..." />
                    </div>
                )}
                <h3 className="text-xl font-semibold text-gray-700 mb-4">{title}</h3>
                <input
                    type="text"
                    placeholder="Filtrovat uživatele..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="mb-4 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <ul className="divide-y divide-gray-200">
                        {filteredUsers.map(user => (
                            <li key={user.id} className="px-6 py-4 flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-800">{user.username}</span>
                                <div className="flex items-center gap-4">
                                    <button onClick={() => handleEditClick(user)} className="text-gray-500 hover:text-blue-600" title="Upravit uživatele">
                                        <EditIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setUserToDelete(user)}
                                        disabled={user.username === 'admin' || user.id === currentUser?.id}
                                        className="text-gray-500 hover:text-red-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                                        title={
                                            user.username === 'admin' 
                                            ? 'Nelze smazat admina' 
                                            : user.id === currentUser?.id 
                                            ? 'Nelze smazat sám sebe' 
                                            : 'Smazat uživatele'
                                        }
                                    >
                                        <DeleteIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </li>
                        ))}
                        {filteredUsers.length === 0 && (
                            <li className="px-6 py-4 text-center text-gray-500">
                                Žádní uživatelé nenalezeni.
                            </li>
                        )}
                    </ul>
                </div>
            </div>
            <ConfirmModal
                isOpen={!!userToDelete}
                onClose={() => setUserToDelete(null)}
                onConfirm={handleConfirmDelete}
                title="Smazat uživatele?"
                confirmText="Smazat"
                confirmClassName="bg-red-600 text-white rounded-md hover:bg-red-700"
            >
                {userToDelete && <p>Opravdu chcete smazat uživatele <strong>{userToDelete.username}</strong>?</p>}
            </ConfirmModal>

            <ConfirmModal
                isOpen={!!userToEdit}
                onClose={() => setUserToEdit(null)}
                onConfirm={handleConfirmEdit}
                isConfirmDisabled={isSubmittingEdit}
                title={`Upravit uživatele ${userToEdit?.username}`}
                confirmText={isSubmittingEdit ? "Ukládám..." : "Uložit změny"}
            >
                {userToEdit && (
                    <div className="space-y-4">
                         <div>
                            <label className="block text-sm font-medium text-gray-700">Uživatelské jméno</label>
                            <input
                                type="text"
                                value={editUsername}
                                onChange={(e) => setEditUsername(e.target.value)}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Nové heslo (nechat prázdné)</label>
                            <input
                                type="password"
                                value={editPassword}
                                onChange={(e) => setEditPassword(e.target.value)}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                placeholder="Změnit heslo..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Potvrdit nové heslo</label>
                            <input
                                type="password"
                                value={editPasswordConfirm}
                                onChange={(e) => setEditPasswordConfirm(e.target.value)}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                            />
                        </div>
                        {editError && <p className="text-sm text-red-600">{editError}</p>}
                    </div>
                )}
            </ConfirmModal>
        </div>
    );
};